<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { type ToastOptions } from 'vue3-toastify'

const props = withDefaults(
  defineProps<{
    closeToast?: () => void
    toastProps?: ToastOptions
    showCloseButton?: boolean
    title: string
    message?: string
    buttonText: string
    buttonAction?: () => void
    buttonVariant?: 'primary' | 'secondary' | 'tertiary'
    icon?: string
    iconClass?: string
    beforeCtaIcon?: string
    afterCtaIcon?: string
    showActionButton?: boolean
  }>(),
  {
    closeToast: undefined,
    toastProps: undefined,
    showCloseButton: true,
    buttonVariant: 'primary',
    message: undefined,
    buttonAction: undefined,
    icon: undefined,
    iconClass: undefined,
    beforeCtaIcon: undefined,
    afterCtaIcon: undefined,
    showActionButton: true
  }
)

const handleButtonClick = (): void => {
  if (props.buttonAction) {
    props.buttonAction()
  }
  if (props.closeToast) {
    props.closeToast()
  }
}
</script>

<template>
  <div class="action-toast-root">
    <FontAwesomeIcon
      v-if="icon?.length"
      :icon="icon"
      class="icon-size-1 toast-icon"
      :class="iconClass" />

    <div class="content">
      <strong class="title">{{ title }}</strong>
      <p v-if="message" class="message">{{ message }}</p>
      <button
        v-if="showActionButton"
        class="action-button"
        :class="`btn ${buttonVariant}`"
        @click="handleButtonClick">
        <FontAwesomeIcon
          v-if="beforeCtaIcon?.length"
          :icon="beforeCtaIcon"
          class="icon-size-1" />
        {{ buttonText }}
        <FontAwesomeIcon
          v-if="afterCtaIcon?.length"
          :icon="afterCtaIcon"
          class="icon-size-1" />
      </button>
    </div>

    <button
      v-if="showCloseButton"
      class="btn circle close-button"
      @click="closeToast">
      <FontAwesomeIcon icon="fa-light fa-xmark" class="icon-size-1" />

      <!-- TODO localize -->
      <span class="sr-only">Close toast</span>
    </button>
  </div>
</template>

<style scoped>
.action-toast-root {
  display: flex;
  align-items: flex-start;
  color: var(--gray-12);
}

.toast-icon {
  margin-top: var(--size-1);
  flex-shrink: 0;
}

.content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

strong.title {
  font-weight: 600;
  font-variation-settings: 'wght' 500;
  font-size: var(--font-size-1);
  margin: 0;
  line-height: 1.2;
}

.message {
  margin: 0;
  font-size: var(--font-size-0);
}

.action-button {
  align-self: flex-start;
  border-radius: var(--radius-2);
  font-variation-settings: 'wght' 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  display: flex;
  gap: var(--size-00);
  font-size: var(--font-size-0);
  font-weight: 600;
  color: var(--teal-10);
  align-items: center;
}

.close-button {
  flex-shrink: 0;
  align-self: start;
}
</style>
